// src/redux/slices/customerKycSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL;
const STATIC_USER_ID = '254';
const STATIC_SUPER_ADMIN_ID = '12';

// Auth header helper
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

// ==========================
// Thunks
// ==========================

// POST: Upload Customer KYC
export const uploadCustomerKyc = createAsyncThunk(
  'customerKyc/uploadCustomerKyc',
  async ({ file, docName, status }, thunkAPI) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('docName', docName);
      formData.append('userId', STATIC_USER_ID);
      formData.append('superAdminId', STATIC_SUPER_ADMIN_ID);
      formData.append('status', status);
      formData.append('createdBy', 'User');
      formData.append('createdId', '1');

      const response = await axios.post(
        `${BASE_URL}/create-customer-kyc`,
        formData,
        {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || 'Upload failed');
    }
  }
);

// GET: Fetch Customer Documents
export const fetchCustomerDocuments = createAsyncThunk(
  'customerKyc/fetchCustomerDocuments',
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/list-kyc?userId=${STATIC_USER_ID}`,
        getAuthHeaders()
      );
      return response.data.documents;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || 'Fetch failed');
    }
  }
);

// DELETE: Delete Customer Document
export const deleteCustomerKyc = createAsyncThunk(
  'customerKyc/deleteCustomerKyc',
  async ({ id, createdId }, thunkAPI) => {
    try {
      const url = `${BASE_URL}/delete-customer-kyc/${id}?userId=${STATIC_USER_ID}&superAdminId=${STATIC_SUPER_ADMIN_ID}&createdId=${createdId}`;
      await axios.delete(url, getAuthHeaders());
      return id;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || 'Delete failed');
    }
  }
);

// POST: Reject Document
export const rejectCustomerKyc = createAsyncThunk(
  'customerKyc/rejectCustomerKyc',
  async ({ id, status = 'Rejected' }, thunkAPI) => {
    try {
      await axios.post(
        `${BASE_URL}/reject-customer-kyc/${id}`,
        { status },
        getAuthHeaders()
      );
      return { id, status };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || 'Reject failed');
    }
  }
);

// POST: Approve Document
export const approveCustomerKyc = createAsyncThunk(
  'customerKyc/approveCustomerKyc',
  async (id, thunkAPI) => {
    try {
      await axios.post(
        `${BASE_URL}/approve-customer-kyc/${id}`,
        { status: 'Approved' },
        getAuthHeaders()
      );
      return { id, status: 'Approved' };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || 'Approve failed');
    }
  }
);

// ==========================
// Slice
// ==========================
const customerKycSlice = createSlice({
  name: 'customerKyc',
  initialState: {
    documents: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Upload
      .addCase(uploadCustomerKyc.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadCustomerKyc.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(uploadCustomerKyc.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch
      .addCase(fetchCustomerDocuments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomerDocuments.fulfilled, (state, action) => {
        state.loading = false;
        state.documents = action.payload;
      })
      .addCase(fetchCustomerDocuments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete
      .addCase(deleteCustomerKyc.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCustomerKyc.fulfilled, (state, action) => {
        state.loading = false;
        state.documents = state.documents.filter(doc => doc.id !== action.payload);
      })
      .addCase(deleteCustomerKyc.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Approve
      .addCase(approveCustomerKyc.fulfilled, (state, action) => {
        const { id, status } = action.payload;
        state.documents = state.documents.map(doc =>
          doc.id === id ? { ...doc, status } : doc
        );
      })
      .addCase(approveCustomerKyc.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Reject
      .addCase(rejectCustomerKyc.fulfilled, (state, action) => {
        const { id, status } = action.payload;
        state.documents = state.documents.map(doc =>
          doc.id === id ? { ...doc, status } : doc
        );
      })
      .addCase(rejectCustomerKyc.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export default customerKycSlice.reducer;